import { Link, NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Building2, ClipboardList, Coins, FileCheck2,
  ExternalLink, Menu, X, ShieldCheck, LogOut, Lock,
} from "lucide-react";

const NAV = [
  { to: "/panel-yonetim", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/panel-yonetim/kullanicilar", label: "Kullanıcılar", icon: Users, id: "kullanicilar" },
  { to: "/panel-yonetim/studyolar", label: "Stüdyolar", icon: Building2, id: "studyolar" },
  { to: "/panel-yonetim/rezervasyonlar", label: "Rezervasyonlar", icon: ClipboardList, id: "rezervasyonlar" },
  { to: "/panel-yonetim/kredi-islemleri", label: "Kredi İşlemleri", icon: Coins, id: "kredi-islemleri" },
  { to: "/panel-yonetim/basvurular", label: "Partner Başvuruları", icon: FileCheck2, id: "basvurular" },
];

const ADMIN_USER = "admin";
const ADMIN_PASS = "eslem";

function AdminLogin({ onSuccess }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (u === ADMIN_USER && p === ADMIN_PASS) {
      sessionStorage.setItem("admin_authed", "1");
      onSuccess();
    } else {
      toast.error("Kullanıcı adı veya şifre hatalı");
    }
  };

  const inputCls = "w-full rounded-md border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-white/40";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F172A] px-5 font-ops">
      <form onSubmit={submit} className="w-full max-w-sm rounded-lg border border-white/10 bg-white/[0.03] p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white text-[#0F172A]"><ShieldCheck className="h-6 w-6" /></div>
          <h1 className="mt-4 text-xl font-semibold text-white">XYZ Admin</h1>
          <p className="mt-1 text-sm text-slate-400">Devam etmek için giriş yapın</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-slate-400">Kullanıcı adı</label>
            <input data-testid="admin-login-username" className={inputCls} value={u} onChange={(e) => setU(e.target.value)} placeholder="admin" autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-slate-400">Şifre</label>
            <input data-testid="admin-login-password" type="password" className={inputCls} value={p} onChange={(e) => setP(e.target.value)} placeholder="••••••" />
          </div>
        </div>
        <button data-testid="admin-login-submit" type="submit" className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-white py-2.5 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-slate-200">
          <Lock className="h-4 w-4" /> Giriş yap
        </button>
      </form>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("admin_authed") === "1");

  const logout = () => {
    sessionStorage.removeItem("admin_authed");
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const Sidebar = (
    <div className="flex h-full flex-col bg-[#0F172A] text-slate-300">
      <div className="flex items-center gap-2 px-5 py-6">
        <div className="flex h-8 w-8 items-center justify-center rounded bg-white text-sm font-bold text-[#0F172A]">X</div>
        <div><p className="text-sm font-semibold text-white">XYZ Admin</p><p className="text-xs text-slate-400">Platform Yönetimi</p></div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`
            }
          >
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 space-y-1">
        <button data-testid="admin-logout-button" onClick={logout} className="flex w-full items-center gap-2 rounded border border-white/10 px-3 py-2.5 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
          <LogOut className="h-4 w-4" /> Çıkış yap
        </button>
        <Link to="/" data-testid="admin-back-to-app" className="flex items-center gap-2 rounded border border-white/10 px-3 py-2.5 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
          <ExternalLink className="h-4 w-4" /> Kullanıcı uygulaması
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F1F5F9] font-ops text-[#0F172A]">
      <aside className="fixed inset-y-0 left-0 hidden w-60 lg:block">{Sidebar}</aside>

      <div className="flex items-center justify-between bg-[#0F172A] px-4 py-3 text-white lg:hidden">
        <div className="flex items-center gap-2"><div className="flex h-7 w-7 items-center justify-center rounded bg-white text-xs font-bold text-[#0F172A]">X</div><span className="text-sm font-semibold">XYZ Admin</span></div>
        <button data-testid="admin-menu-toggle" onClick={() => setOpen(!open)}>{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
      </div>
      {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setOpen(false)}><aside className="absolute inset-y-0 left-0 w-60" onClick={(e) => e.stopPropagation()}>{Sidebar}</aside></div>}

      <main className="lg:pl-60">
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
